import { useState } from 'react'
import api from 'services/api'
import { TemplateProduct, TemplateProductWithAlternatives } from './useTemplate'

export interface UnavailableProduct {
  id: number
  name: string
  translated_name?: string
  class_name?: string
  shape_name?: string
  image_url?: string
}

interface ProcessTemplateProductsResult {
  products: TemplateProductWithAlternatives[]
  substitutions: string[]
}

const isProductInactive = (product: any): boolean => {
  if (!product) return false
  return product.status_assistant === 'I'
}

const toUnavailableProduct = (product: any): UnavailableProduct => ({
  id: product.id,
  name: product.name ?? '',
  translated_name: product.translated_name ?? '',
  class_name: product.class?.name ?? product.class_name ?? '',
  shape_name: product.shape?.name ?? product.shape_name ?? '',
  image_url: product.media?.find((m: any) => m.format === 'IMG')?.path ?? product.image ?? ''
})

const findAlternativeProduct = async (originalProduct: any): Promise<any | null> => {
  try {
    const mainProductId = originalProduct.main_product_id || originalProduct.id
    const { data } = await api.get(`/get_product_flavors/${mainProductId}`)
    if (!data?.success || !data?.productFlavors) return null

    const originalFlavorId = originalProduct.flavor_id ?? originalProduct.flavor?.id
    const alternatives = data.productFlavors
      .filter((p: any) => {
        if (p.id === originalProduct.id) return false
        const isActive = p.status === 'A'
        const hasStock = !p.stock || p.stock > 0
        return isActive && hasStock
      })
      .sort((a: any, b: any) => {
        const aHasSameFlavor = a.flavor?.id && a.flavor.id === originalFlavorId
        const bHasSameFlavor = b.flavor?.id && b.flavor.id === originalFlavorId
        if (aHasSameFlavor && !bHasSameFlavor) return -1
        if (!aHasSameFlavor && bHasSameFlavor) return 1
        return (b.stock || 0) - (a.stock || 0)
      })

    return alternatives[0] ?? null
  } catch (err) {
    console.error(err)
    return null
  }
}

const substituteTemplateProduct = (templateProduct: TemplateProduct, alternative: any): TemplateProduct => ({
  ...templateProduct,
  product_id: alternative.id,
  product: {
    ...templateProduct.product,
    ...alternative,
    status_assistant: 'A'
  }
})

const substitutionMessage = (originalProduct: any, alternative: any): string =>
  `O produto "${originalProduct.name}" foi substituído por "${alternative.name}" porque o produto original está inativo.`

export default function useProductSubstitution() {
  const [unavailableProducts, setUnavailableProducts] = useState<UnavailableProduct[]>([])
  const [isUnavailableModalOpen, setIsUnavailableModalOpen] = useState(false)

  const processTemplateProducts = async (
    templateProducts: TemplateProductWithAlternatives[]
  ): Promise<ProcessTemplateProductsResult> => {
    const availableProducts: TemplateProductWithAlternatives[] = []
    const unavailable: UnavailableProduct[] = []
    const substitutions: string[] = []

    for (const templateProduct of templateProducts) {
      if (!templateProduct.product) continue

      const isCombo = (templateProduct.product as any).is_combo

      if (isCombo && (templateProduct.alternatives?.length ?? 0) > 0) {
        // Combo: filhos ficam em `alternatives`. Se qualquer parte estiver inativa
        // e sem substituto, o combo inteiro é removido para não quebrar a composição.
        if (isProductInactive(templateProduct.product)) {
          unavailable.push(toUnavailableProduct(templateProduct.product))
          continue
        }

        const newChildren: TemplateProduct[] = []
        let comboIsAvailable = true
        for (const child of templateProduct.alternatives) {
          if (!child.product) continue
          if (!isProductInactive(child.product)) {
            newChildren.push(child)
            continue
          }
          const alternative = await findAlternativeProduct(child.product)
          if (alternative) {
            substitutions.push(substitutionMessage(child.product, alternative))
            newChildren.push(substituteTemplateProduct(child, alternative))
          } else {
            unavailable.push(toUnavailableProduct(child.product))
            comboIsAvailable = false
            break
          }
        }

        if (comboIsAvailable) {
          availableProducts.push({ ...templateProduct, alternatives: newChildren })
        }
        continue
      }

      // Produto comum: `alternatives` são os produtos ligados por "OU"
      let mainProduct: TemplateProduct | null = templateProduct
      if (isProductInactive(templateProduct.product)) {
        const alternative = await findAlternativeProduct(templateProduct.product)
        if (alternative) {
          substitutions.push(substitutionMessage(templateProduct.product, alternative))
          mainProduct = substituteTemplateProduct(templateProduct, alternative)
        } else {
          unavailable.push(toUnavailableProduct(templateProduct.product))
          mainProduct = null
        }
      }

      const orAlternatives: TemplateProduct[] = []
      for (const orAlternative of templateProduct.alternatives ?? []) {
        if (!orAlternative.product) continue
        if (!isProductInactive(orAlternative.product)) {
          orAlternatives.push(orAlternative)
          continue
        }
        const alternative = await findAlternativeProduct(orAlternative.product)
        if (alternative) {
          substitutions.push(substitutionMessage(orAlternative.product, alternative))
          orAlternatives.push(substituteTemplateProduct(orAlternative, alternative))
        } else {
          unavailable.push(toUnavailableProduct(orAlternative.product))
        }
      }

      if (mainProduct) {
        availableProducts.push({ ...mainProduct, alternatives: orAlternatives })
      } else if (orAlternatives.length > 0) {
        // Produto principal removido: promove a primeira alternativa "OU" disponível
        const [promoted, ...remaining] = orAlternatives
        availableProducts.push({ ...promoted, alternatives: remaining })
      }
    }

    if (unavailable.length > 0) {
      setUnavailableProducts(unavailable)
      setIsUnavailableModalOpen(true)
    }

    return { products: availableProducts, substitutions }
  }

  const closeUnavailableModal = () => {
    setIsUnavailableModalOpen(false)
    setUnavailableProducts([])
  }

  return {
    processTemplateProducts,
    unavailableProducts,
    isUnavailableModalOpen,
    closeUnavailableModal
  }
}
